import { AxiosResponse } from "axios";
import { oc } from "ts-optchain";
import { Nullable } from "@/types";

import { DealernetApi } from "./DealernetApi";
import { TmsService } from "./TmsService";

const CONTROLLER_PATH = "OemManuals";

export class OemManualsService {

  // Get OemManuals Truck Types
  public static async GetTruckTypes(userId: number, brandId: number, productGroupId?: Nullable<number>): Promise<IOemManualsProductGroupsResponseDto> {
    const response = await DealernetApi.client().request({
      method: "post",
      url: `${CONTROLLER_PATH}/TruckTypes`,
      data: { userId, brandId, productGroupId },
    });

    return oc(response as AxiosResponse<IOemManualsProductGroupsResponseDto>).data()!;
  }

  // Get OemManuals Models
  public static async GetModels(userId: number, brandId: number, mcfCostCenterId: number, productGroupId?: Nullable<number>): Promise<IOemManualsProductsResponseDto> {
    const response = await DealernetApi.client().request({
      method: "post",
      url: `${CONTROLLER_PATH}/Models`,
      data: { userId, brandId, mcfCostCenterId, productGroupId },
    });
    return oc(response as AxiosResponse<IOemManualsProductsResponseDto>).data()!;
  }

  // Get OemManuals Component Groups
  public static async GetComponentGroups(userId: number, brandId: number, productId: number, regionId: number, truckTypeId: number): Promise<IOemManualsComponentGroupsResponseDto> {
    const response = await DealernetApi.client().request({
      method: "post",
      url: `${CONTROLLER_PATH}/ComponentGroups`,
      data: { userId, brandId, productId, regionId, truckTypeId },
    });
    return oc(response as AxiosResponse<IOemManualsComponentGroupsResponseDto>).data()!;
  }

  // Get OemManuals Components
    public static async GetComponents(publicationId: number, dataSource: string, bomId: number): Promise<any> {
    const response = await DealernetApi.client().request({
      method: "post",
      url: `${CONTROLLER_PATH}/Components`,
      data: { publicationId, dataSource, bomId },
    });

    return oc(response as AxiosResponse<any>).data()!;
  }


  // Get OemManuals Parts (returns hierarchical tree; leaf nodes are parts)
  public static async GetParts(userId: number, brandId: number, componentNumber: string): Promise<IOemManualsPartTreeNode[]> {
    const response = await DealernetApi.client().request({
      method: "post",
      url: `${CONTROLLER_PATH}/Parts`,
      data: { userId, brandId, componentNumber },
    });

    return oc(response as AxiosResponse<IOemManualsPartTreeNode[]>).data()!;
  }

  /** Recursively extracts leaf parts (nodes with PartId & PartNumber) from a parts tree. */
  public static ExtractLeafParts(nodes: IOemManualsPartTreeNode[]): IOemManualsPartTreeNode[] {
    const leaves: IOemManualsPartTreeNode[] = [];
    for (const node of nodes) {
      if (node.PartId != null && node.PartNumber) {
        leaves.push(node);
      }
      if (node.Components?.length) {
        leaves.push(...OemManualsService.ExtractLeafParts(node.Components));
      }
    }
    return leaves;
  }

  // Get OemManuals Diagram
  public static async GetDiagram(userId: number, brandId: number, componentNumber: string, diagramFormat?: Nullable<OemManualsDiagramFormat>): Promise<IOemManualsComponentDiagramResponseDto> {
    const response = await DealernetApi.client().request({
      method: "post",
      url: `${CONTROLLER_PATH}/Diagram`,
      data: { userId, brandId, componentNumber, diagramFormat },
    });

    return oc(response as AxiosResponse<IOemManualsComponentDiagramResponseDto>).data()!;
  }
}

// Nullable type helper
type Nullable<T> = T | null;

// ============================================================================
// Enums
// ============================================================================

export enum OemManualsDiagramFormat {
  Png = 'png',
  Svg = 'svg'
}

export enum OemManualsErrorType {
  User = 'User',
  Temp = 'Temp',
  System = 'System'
}

// ============================================================================
// Common/Shared Interfaces
// ============================================================================

export interface IOemManualsHeaderDto {
  UUID: string;
  accountNumber: string;
  locationId: string;
  language: string;
}

export interface IOemManualsErrorDto {
  Type: OemManualsErrorType;
  Message: string;
}

export interface IOemManualsBaseRequestDto {
  userId: number;
}

export interface IOemManualsBaseResponseDto {
  statusCode: number;
  responseHeader: IOemManualsHeaderDto;
  error?: Nullable<IOemManualsErrorDto>;
}

// ============================================================================
// Request Interfaces
// ============================================================================

export interface IOemManualsProductGroupsRequestDto extends IOemManualsBaseRequestDto {
  brandId: number;
  productGroupId?: Nullable<number>;
}

export interface IOemManualsProductsRequestDto extends IOemManualsBaseRequestDto {
  brandId: number;
  productGroupId?: Nullable<number>;
}

export interface IOemManualsComponentGroupsRequestDto extends IOemManualsBaseRequestDto {
  brandId: number;
  productId: string;
}

export interface IOemManualsComponentsRequestDto extends IOemManualsBaseRequestDto {
  brandId: number;
  componentGroupName: string;
}

export interface IOemManualsComponentPartsRequestDto extends IOemManualsBaseRequestDto {
  brandId: number;
  componentNumber: string;
}

export interface IOemManualsComponentDiagramRequestDto extends IOemManualsBaseRequestDto {
  brandId: number;
  componentNumber: string;
  diagramFormat?: Nullable<OemManualsDiagramFormat>;
}

// ============================================================================
// Response DTOs (List Items)
// ============================================================================

export interface IOemManualsProductGroupDto {
  TruckClass: string;
  TruckClassId: number;
}

export interface IOemManualsProductDto {
  ModelIdAndSerialNumberRangeId: string;
  ModelName: string;
}

export interface IOemManualsComponentGroupDto {
  PublicationIdAndDataSource: string;
  PublicationModuleTitle: string;
}

export interface IOemManualsComponentDto {
  PublicationIdAndDataSource: string;
  PublicationModuleTitle: string;
  HasChildComponents: boolean;
  ChildComponents: IOemManualsComponentDto[];
}

export interface IOemManualsComponentPartDto {
  OemPartCode: string;
  PartDescription: string;
  Quantity: string;
  ReferenceNumber: string;
  Notes: string;
}

/** Recursive tree node returned by the GetParts API. Leaf nodes have MasterPartId & PartNumber. */
export interface IOemManualsPartTreeNode {
  SortOrder?: number;
  FullHierarchyPath: string;
  PartDescription: string;
  Components: IOemManualsPartTreeNode[];
  /** Present only on leaf (part) nodes. */
  PartId?: number;
  /** Present only on leaf (part) nodes. */
  PartNumber?: string;
}

// ============================================================================
// Response Interfaces (Root)
// ============================================================================

export interface IOemManualsProductGroupsResponseDto extends IOemManualsBaseResponseDto {
  ProductGroups: IOemManualsProductGroupDto[];
}

export interface IOemManualsProductsResponseDto extends IOemManualsBaseResponseDto {
  Products: IOemManualsProductDto[];
}

export interface IOemManualsComponentGroupsResponseDto extends IOemManualsBaseResponseDto {
  ComponentGroups: IOemManualsComponentGroupDto[];
}

export interface IOemManualsComponentsResponseDto extends IOemManualsBaseResponseDto {
  Components: IOemManualsComponentDto[];
}

export interface IOemManualsComponentPartsResponseDto extends IOemManualsBaseResponseDto {
  ComponentParts: IOemManualsComponentPartDto[];
}

export interface IOemManualsComponentDiagramResponseDto extends IOemManualsBaseResponseDto {
  DiagramData: string;
  DiagramFormat: OemManualsDiagramFormat;
}

